import { lineHexColorFromId } from '../interaction/stationResolver'
import { elementVisibleInMode, type SchemaData } from '../schema/types'
import { outlinedLineIdsFromSchema } from '../state/useRouteDetailsPayload'
import { layoutLineDiagram } from './layoutLineDiagram'
import type { CityConfig } from '../city/CityConfig'
import type { LineCaption, RailCell, RailCurve } from './LineDiagram'
import { lineDiagramFor } from './lineDiagramFor'
import { lineSlugGraph } from './lineSlugGraph'
import { lineTitleParts } from './lineTitleParts'
import { LineUrlKey } from './LineUrlKey'

export interface LineViewStationRow {
  readonly kind: 'station'
  readonly slug: string
  /** Schema record the row opens (the line's first platform at that slug). */
  readonly stationId: string
  readonly name: string
  /** Every other line at the station (Tube, Overground, Elizabeth, rail…), by name. */
  readonly transfers: readonly string[]
  /** A terminus of some rail — bold name (route-detail first / last stop). */
  readonly terminus: boolean
  readonly cells: readonly RailCell[]
  readonly curves: readonly RailCurve[]
}

export interface LineViewCaptionRow {
  readonly kind: 'caption'
  readonly caption: LineCaption
  /** Station names behind `caption.slugs` (empty for a loop caption). */
  readonly names: readonly string[]
  readonly cells: readonly RailCell[]
}

export interface LineViewLoopCloseRow {
  readonly kind: 'loopClose'
  readonly slug: string
  readonly stationId: string
  readonly name: string
  readonly cells: readonly RailCell[]
}

export type LineViewRow = LineViewStationRow | LineViewCaptionRow | LineViewLoopCloseRow

/** Everything the line panel draws for one line, resolved from the schema. */
export interface LineView {
  readonly lineId: string
  readonly slug: string
  /** Title name per the city's rules: "Bakerloo", "DLR", "2 · 7 Avenue Express". */
  readonly name: string
  /** Reads "<name> line" (London Tube and Overground). */
  readonly nameTakesLineSuffix: boolean
  readonly color: string
  readonly outlined: boolean
  readonly rails: number
  readonly stationCount: number
  /** Fare zones the line runs through, `"1"` or `"1–5"`; null when unknown. */
  readonly zones: string | null
  readonly zoneCount: number
  readonly rows: readonly LineViewRow[]
}

function zoneRange(
  zones: readonly (string | null | undefined)[],
): { label: string; count: number } | null {
  const numbers = zones.flatMap((zone) => (zone ?? '').match(/\d+/g)?.map(Number) ?? [])
  if (numbers.length === 0) return null
  const low = Math.min(...numbers)
  const high = Math.max(...numbers)
  return low === high
    ? { label: `${low}`, count: 1 }
    : { label: `${low}–${high}`, count: high - low + 1 }
}

/**
 * The other lines stopping at `slug`, in schema order, each once — by name,
 * or by route bullet where the city titles that line type with it (New York:
 * "1 · 2 · 3", not three route names of which two are the same).
 */
function transfersAt(
  schema: SchemaData,
  slug: string,
  lineId: string,
  rules: CityConfig['lineTitles'],
): string[] {
  const names = new Map<string, string>()
  for (const station of schema.stations) {
    const line = station.line
    if (station.slug !== slug || line.id === lineId || names.has(line.id)) continue
    // A late-night train stopping here (TASK-1136) is no day transfer.
    if (!elementVisibleInMode(station.modes, 'normal')) continue
    const bullet = rules.shortNameTypes.includes(line.type) ? line.shortLocalName : undefined
    names.set(line.id, bullet !== undefined && bullet !== '' ? bullet : (line.localName ?? line.id))
  }
  return [...new Set(names.values())]
}

/**
 * Resolves the line view of `lineId`: its name, colour, zones and the rail
 * diagram laid out into rows with station names and transfers. `null` when
 * the line has no diagram in this city (`lineDiagramFor`).
 */
export function buildLineView(
  schema: SchemaData,
  lineId: string,
  theme: 'light' | 'dark',
  city: Pick<CityConfig, 'lineDiagrams' | 'lineTitles'>,
): LineView | null {
  const spec = lineDiagramFor(schema, lineId, city.lineDiagrams)
  if (spec === null) return null
  const { stationBySlug } = lineSlugGraph(schema, lineId)
  const first = stationBySlug.values().next().value
  if (first === undefined) return null

  const nameOf = (slug: string): string => stationBySlug.get(slug)?.localName ?? slug
  const idOf = (slug: string): string => stationBySlug.get(slug)?.id ?? slug
  const rows: LineViewRow[] = layoutLineDiagram(spec).map((row) => {
    if (row.kind === 'caption') {
      return {
        ...row,
        names: row.caption.kind === 'loop' ? [] : row.caption.slugs.map(nameOf),
      }
    }
    if (row.kind === 'loopClose') {
      return { ...row, stationId: idOf(row.slug), name: nameOf(row.slug) }
    }
    return {
      ...row,
      stationId: idOf(row.slug),
      name: nameOf(row.slug),
      transfers: transfersAt(schema, row.slug, lineId, city.lineTitles),
      terminus: row.cells.some((cell) => cell === 'start' || cell === 'end'),
    }
  })

  const title = lineTitleParts(first.line, city.lineTitles)
  const zones = zoneRange(
    schema.stations
      .filter((s) => s.line.id === lineId && elementVisibleInMode(s.modes, 'normal'))
      .map((s) => s.zone),
  )
  return {
    lineId,
    slug: LineUrlKey.of(schema, lineId),
    name: title.name,
    nameTakesLineSuffix: title.takesSuffix,
    color: lineHexColorFromId(schema, lineId, theme) ?? first.line.hexColor,
    outlined: outlinedLineIdsFromSchema(schema).has(lineId),
    rails: spec.rails,
    stationCount: stationBySlug.size,
    zones: zones?.label ?? null,
    zoneCount: zones?.count ?? 0,
    rows,
  }
}
